/** Cabecera común de las pantallas de contabilidad PGC: título, emisor, ejercicio y aviso si no hay nada activo. */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen } from 'lucide-react'
import type { IssuerDto } from '@toga/shared'
import { selectCls, yearOptions } from '../accounting/common'
import { useAuth } from '../../contexts/AuthContext'
import PendingBanner from './PendingBanner'

interface Props {
  title: string
  description: string
  pgc: IssuerDto[]
  loaded: boolean
  issuerId: string
  onIssuer: (id: string) => void
  year?: number
  onYear?: (y: number) => void
  children?: ReactNode
}

export default function CtbHeader({
  title,
  description,
  pgc,
  loaded,
  issuerId,
  onIssuer,
  year,
  onYear,
  children,
}: Props) {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  return (
    <header className="space-y-3">
      <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
        <BookOpen size={20} className="text-toga-300" />
        {title}
      </h1>
      <p className="t-muted text-sm max-w-3xl">{description}</p>
      {loaded && pgc.length === 0 ? (
        <div className="surface px-5 py-6 text-sm t-muted max-w-2xl">
          <p className="t-primary font-medium">
            Ningún emisor lleva su contabilidad por partida doble en Toga.
          </p>
          <p className="mt-1">
            {isAdmin ? (
              <>
                Actívala en{' '}
                <Link to="/settings/billing" className="text-toga-300 hover:underline">
                  Ajustes → Facturación → Emisores
                </Link>{' '}
                con «Activar contabilidad PGC»: prepara el plan de cuentas y los ejercicios.
              </>
            ) : (
              'Pide a un administrador que la active en Ajustes → Facturación → Emisores.'
            )}
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {pgc.length > 1 && (
            <select
              value={issuerId}
              onChange={(e) => onIssuer(e.target.value)}
              className={selectCls}
              aria-label="Emisor"
            >
              {pgc.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} · {i.nif}
                </option>
              ))}
            </select>
          )}
          {pgc.length === 1 && (
            <span className="text-xs px-2.5 py-1 rounded-full border border-base t-secondary">
              {pgc[0]!.name} · {pgc[0]!.nif}
            </span>
          )}
          {year !== undefined && onYear && (
            <select
              value={year}
              onChange={(e) => onYear(Number(e.target.value))}
              className={selectCls}
              aria-label="Ejercicio"
            >
              {yearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
          {children}
        </div>
      )}
      {issuerId && pgc.length > 0 && <PendingBanner issuerId={issuerId} />}
    </header>
  )
}
